/* eslint-disable react/prop-types */
/**
 * InsuranceAdminDialogs — the admin "Manage Insurance" dialog (payer list,
 * search, card/list toggle, archive/restore, export/import, audit log,
 * migrate) and the Add/Edit Insurance form dialogs (with the shared
 * ``renderInsuranceFormFields`` renderer and the optimistic create/update
 * handlers), extracted VERBATIM from App.js (Jun 2026, strangler pattern).
 * Hosts: legacy App.js Admin panel AND V2 ManageInsuranceAdminRoute.
 */
import React, { lazy, Suspense } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Archive, ArrowRight, Clock, Download, Edit, FileText, History, LayoutGrid,
  List, Plus, RotateCcw, Search, Shield, Trash2, Upload,
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { DebouncedInput } from '../ui/debounced-textarea';
import { PayerModifierRulesEditor } from './PayerModifierRulesEditor';
import { SecondaryRoutingRulesEditor } from './SecondaryRoutingRulesEditor';

const PayerAuditLog = lazy(() => import('../admin/PayerAuditLog'));

export const ManageInsuranceDialog = ({ ctx }) => {
  const {
    API,
    insurances,
    fetchInsurances,
    setEditingInsurance,
    setInsuranceFormData,
    setShowAddInsurance,
    setShowEditInsurance,
    setMigratingInsurance,
    showPayerAuditLog,
    setShowPayerAuditLog,
    insuranceViewMode,
    setInsuranceViewMode,
    showArchivedInsurances,
    setShowArchivedInsurances,
    showManageInsurance,
    setShowManageInsurance,
    headerExtras,
    refreshNotes,
    refreshClients,
    refreshStats,
  } = ctx;

  // Sep 2026 — quick filter by name, abbreviation, or payer ID.
  const [insuranceSearch, setInsuranceSearch] = React.useState('');
  const matchesSearch = (insurance) => {
    const q = insuranceSearch.trim().toLowerCase();
    if (!q) return true;
    return (insurance.name || '').toLowerCase().includes(q)
      || (insurance.abbreviation || '').toLowerCase().includes(q)
      || String(insurance.payer_id || '').toLowerCase().includes(q);
  };
  const visibleInsurances = (insurances || []).filter(
    (i) => (showArchivedInsurances || i.status !== 'archived') && matchesSearch(i)
  );

  return (
      <Dialog open={showManageInsurance} onOpenChange={setShowManageInsurance}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Insurance Management</DialogTitle>
            {headerExtras || null}
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Insurance Companies */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Insurance Companies</h3>
                <div className="flex space-x-2">
                  {/* View Mode Toggle */}
                  <div className="flex border rounded-lg overflow-hidden mr-2">
                    <Button
                      size="sm"
                      variant={insuranceViewMode === 'list' ? 'default' : 'ghost'}
                      onClick={() => setInsuranceViewMode('list')}
                      className={insuranceViewMode === 'list' ? 'bg-teal-600' : ''}
                    >
                      <List className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant={insuranceViewMode === 'grid' ? 'default' : 'ghost'}
                      onClick={() => setInsuranceViewMode('grid')}
                      className={insuranceViewMode === 'grid' ? 'bg-teal-600' : ''}
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/insurances/template/csv`, {
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'insurance_template.csv');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success("Template downloaded - see examples and instructions inside!");
                      } catch (error) {
                        toast.error("Failed to download template");
                      }
                    }}
                    title="Download CSV template with examples and valid values"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download Template
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/insurances/export/csv`, {
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'insurance_companies.csv');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        toast.success("Current insurances exported successfully");
                      } catch (error) {
                        toast.error("Failed to download CSV");
                      }
                    }}
                    title="Export current insurance companies"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Export Current
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => document.getElementById('insurance-csv-upload').click()}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Upload CSV
                  </Button>
                  <input
                    id="insurance-csv-upload"
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        const response = await axios.post(`${API}/insurances/import/csv`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        const { created, skipped, errors } = response.data;
                        let message = `CSV Import Complete:\n✅ Created: ${created}\n⏭️ Skipped (already exists): ${skipped}`;
                        if (errors.length > 0) {
                          message += `\n❌ Errors: ${errors.length}\n${errors.join('\n')}`;
                        }
                        
                        if (errors.length > 0) {
                          toast.error(message, { duration: 10000 });
                        } else {
                          toast.success(message);
                        }
                        
                        // Refresh data
                        refreshNotes(); refreshClients(); refreshStats();
                        
                        // Reset file input
                        e.target.value = '';
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Failed to upload CSV");
                        e.target.value = '';
                      }
                    }}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={showArchivedInsurances || false}
                      onChange={(e) => setShowArchivedInsurances(e.target.checked)}
                      className="h-4 w-4"
                    />
                    Show Archived
                  </label>
                  <Button
                    size="sm"
                    variant={showPayerAuditLog ? "default" : "outline"}
                    onClick={() => setShowPayerAuditLog(!showPayerAuditLog)}
                    data-testid="payer-audit-log-toggle"
                    className={showPayerAuditLog ? "bg-blue-600 hover:bg-blue-700" : ""}
                  >
                    <History className="h-4 w-4 mr-1" />
                    {showPayerAuditLog ? 'Hide Audit Log' : 'Audit Log'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowAddInsurance(true)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Insurance
                  </Button>
                </div>
              </div>
              
              <div className="space-y-3">
                {/* Search (Sep 2026) — filter by name or payer ID */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={insuranceSearch}
                    onChange={(e) => setInsuranceSearch(e.target.value)}
                    placeholder="Search insurances by name or payer ID..."
                    className="pl-9"
                    data-testid="insurance-search-input"
                  />
                </div>
                {insuranceSearch && (
                  <p className="text-xs text-gray-500" data-testid="insurance-search-count">
                    {visibleInsurances.length} match{visibleInsurances.length === 1 ? '' : 'es'}
                  </p>
                )}
                {/* Payer Profile Audit Log (toggle) */}
                {showPayerAuditLog && (
                  <Suspense fallback={<div className="text-center py-4 text-gray-400 text-sm">Loading audit log...</div>}>
                    <PayerAuditLog
                      token={sessionStorage.getItem('token')}
                      insurances={insurances || []}
                    />
                  </Suspense>
                )}
                {/* List View */}
                {insuranceViewMode === 'list' && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payer ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plans</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Billing Types</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filing Limits</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {visibleInsurances.map(insurance => (
                          <tr key={insurance.id} className={`hover:bg-gray-50 ${insurance.status === 'archived' ? 'opacity-60' : ''}`}>
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900 flex items-center gap-2">
                                {insurance.name}
                                {insurance.abbreviation && <span className="text-gray-500 text-sm">({insurance.abbreviation})</span>}
                                {insurance.status === 'archived' && <Badge variant="secondary" className="text-xs">Archived</Badge>}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{insurance.payer_id}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {insurance.plans?.slice(0, 2).map((plan, idx) => {
                                  const planName = typeof plan === 'string' ? plan : plan.name;
                                  return (
                                    <Badge key={idx} variant="secondary" className="text-xs">{planName}</Badge>
                                  );
                                })}
                                {insurance.plans?.length > 2 && (
                                  <Badge variant="outline" className="text-xs">+{insurance.plans.length - 2}</Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {insurance.payer_billing_types?.map((type, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs capitalize">{type}</Badge>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600">
                              <div>Initial: {insurance.initial_filing_days || 90}d</div>
                              <div>Appeal: {insurance.appeal_deadline_days || 60}d</div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={insurance.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                                {insurance.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {/* Actions parity with card view: Edit + Archive/Restore + Delete.
                                  Behavior identical to lines ~64363-64426 to keep both views in lockstep. */}
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  title="Edit"
                                  onClick={() => {
                                    setEditingInsurance(insurance);
                                    setInsuranceFormData({
                                      name: insurance.name || '',
                                      abbreviation: insurance.abbreviation || '',
                                      payer_id: insurance.payer_id || '',
                                      eligibility_payer_id: insurance.eligibility_payer_id || '',
                                      claims_payer_id: insurance.claims_payer_id || '',
                                      era_payer_id: insurance.era_payer_id || '',
                                      secondary_identifier: insurance.secondary_identifier || '',
                                      cob_use_carrier_code: insurance.cob_use_carrier_code === true,
                                      plans: insurance.plans || [],
                                      payer_billing_types: insurance.payer_billing_types || [],
                                      assigned_npis: insurance.assigned_npis || [],
                                      service_facility_npis: insurance.service_facility_npis || [],
                                      authorization_exemptions: insurance.authorization_exemptions || [],
                                      modifier_rules: insurance.modifier_rules || [],
                                      secondary_routing_rules: insurance.secondary_routing_rules || [],
                                      contact_info: insurance.contact_info || { phone: '', email: '', address: '' },
                                      initial_filing_days: insurance.initial_filing_days || 90,
                                      corrected_claim_days: insurance.corrected_claim_days || 90,
                                      appeal_deadline_days: insurance.appeal_deadline_days || 60,
                                      timely_filing_notes: insurance.timely_filing_notes || '',
                                      rendering_npi_type: insurance.rendering_npi_type || 'organization',
                                      requires_taxonomy: insurance.requires_taxonomy || false,
                                      search_strategy: insurance.search_strategy || 'subscriber_dependent',
                                      strip_member_id_suffix: insurance.strip_member_id_suffix !== false,
                                      strip_member_id_prefix: insurance.strip_member_id_prefix || '',
                                      include_provider_address: insurance.include_provider_address !== false,
                                      send_zip_plus4: insurance.send_zip_plus4 === true,
                                      default_billing_entity_type: insurance.default_billing_entity_type || null,
                                      box31_signature: insurance.box31_signature || null,
                                      suppress_2310b_for_facility_billing: insurance.suppress_2310b_for_facility_billing === true,
                                      suppress_service_facility_loop: insurance.suppress_service_facility_loop === true,
                                      suppress_2310c_when_billing_address_matches: insurance.suppress_2310c_when_billing_address_matches === true,
                                      suppress_2310c_npi_if_same_as_billing: insurance.suppress_2310c_npi_if_same_as_billing === true,
                                      insurance_type_code: insurance.insurance_type_code || null,
                                      loop_2310b_override: insurance.loop_2310b_override || null,
                                      clearinghouse: insurance.clearinghouse || 'availity',
                                      claims_clearinghouse: insurance.claims_clearinghouse || null,
                                      trizetto_claims_payer_id: insurance.trizetto_claims_payer_id || '',
                                      trizetto_eligibility_payer_id: insurance.trizetto_eligibility_payer_id || ''
                                    });
                                    setShowEditInsurance(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {insurance.status !== 'archived' ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-amber-600 hover:bg-amber-50"
                                    title="Archive"
                                    onClick={async () => {
                                      if (window.confirm(`Archive "${insurance.name}"? It will be hidden from dropdowns but existing client records will be preserved.`)) {
                                        try {
                                          await axios.patch(`${API}/insurances/${insurance.id}`, { status: 'archived' }, {
                                            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                          });
                                          toast.success('Insurance archived');
                                          fetchInsurances();
                                        } catch (error) {
                                          toast.error('Failed to archive insurance');
                                        }
                                      }
                                    }}
                                  >
                                    <Archive className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 hover:bg-green-50"
                                    title="Restore"
                                    onClick={async () => {
                                      try {
                                        await axios.patch(`${API}/insurances/${insurance.id}`, { status: 'active' }, {
                                          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                        });
                                        toast.success('Insurance restored');
                                        fetchInsurances();
                                      } catch (error) {
                                        toast.error('Failed to restore insurance');
                                      }
                                    }}
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 hover:bg-blue-50"
                                  title="Migrate clients off this insurance"
                                  onClick={() => setMigratingInsurance({ id: insurance.id, name: insurance.name })}
                                >
                                  <ArrowRight className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:bg-red-50"
                                  title="Delete"
                                  onClick={async () => {
                                    if (window.confirm(`Delete "${insurance.name}"? This can only be done if no claims have been billed under this insurance.`)) {
                                      try {
                                        await axios.delete(`${API}/insurances/${insurance.id}`, {
                                          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                        });
                                        toast.success('Insurance deleted');
                                        fetchInsurances();
                                      } catch (error) {
                                        toast.error(error.response?.data?.detail || 'Cannot delete - insurance has billing history');
                                      }
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(!insurances || insurances.length === 0) && (
                      <div className="text-center py-8">
                        <p className="text-gray-500 mb-4">No insurance companies configured</p>
                        <Button
                          onClick={() => setShowAddInsurance(true)}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Your First Insurance Company
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Grid View */}
                {insuranceViewMode === 'grid' && visibleInsurances.map(insurance => (
                  <Card key={insurance.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-lg flex items-center gap-2">
                          {insurance.name}
                          {insurance.abbreviation && (
                            <span className="text-sm text-gray-500">({insurance.abbreviation})</span>
                          )}
                          {insurance.status === 'archived' && (
                            <Badge variant="secondary" className="bg-gray-200 text-gray-600">Archived</Badge>
                          )}
                        </h4>
                        <p className="text-sm text-gray-600">Payer ID: {insurance.payer_id}</p>
                        {insurance.plans && insurance.plans.length > 0 && (
                          <div className="mt-2">
                            <Label className="text-xs text-gray-500">Plans ({insurance.plans.length})</Label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {insurance.plans.map((plan, idx) => {
                                // Handle both string and object format
                                const planName = typeof plan === 'string' ? plan : plan.name;
                                const planSecId = typeof plan === 'object' ? plan.secondary_identifier : null;
                                return (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {planName}
                                    {planSecId && <span className="ml-1 text-gray-500">[{planSecId}]</span>}
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="mt-2">
                          <Label className="text-xs text-gray-500">Billing Types</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {insurance.payer_billing_types?.map((type, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs capitalize">{type}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="mt-2">
                          <Label className="text-xs text-gray-500">Assigned NPIs ({insurance.assigned_npis?.length || 0})</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {insurance.assigned_npis?.map((npi, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">{npi}</Badge>
                            ))}
                            {(!insurance.assigned_npis || insurance.assigned_npis.length === 0) && (
                              <span className="text-xs text-gray-400">No NPIs assigned</span>
                            )}
                          </div>
                        </div>
                        {/* Timely Filing Limits */}
                        <div className="mt-2 pt-2 border-t">
                          <Label className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Timely Filing Limits
                          </Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <Badge variant="outline" className="text-xs bg-blue-50">
                              Initial: {insurance.initial_filing_days || 90} days
                            </Badge>
                            <Badge variant="outline" className="text-xs bg-orange-50">
                              Corrected: {insurance.corrected_claim_days || 90} days
                            </Badge>
                            <Badge variant="outline" className="text-xs bg-purple-50">
                              Appeal: {insurance.appeal_deadline_days || 60} days
                            </Badge>
                          </div>
                          {insurance.timely_filing_notes && (
                            <p className="text-xs text-gray-500 mt-1 italic">{insurance.timely_filing_notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingInsurance(insurance);
                            setInsuranceFormData({
                              name: insurance.name || '',
                              abbreviation: insurance.abbreviation || '',
                              payer_id: insurance.payer_id || '',
                              eligibility_payer_id: insurance.eligibility_payer_id || '',
                              claims_payer_id: insurance.claims_payer_id || '',
                              era_payer_id: insurance.era_payer_id || '',
                              secondary_identifier: insurance.secondary_identifier || '',
                              cob_use_carrier_code: insurance.cob_use_carrier_code === true,
                              plans: insurance.plans || [],
                              payer_billing_types: insurance.payer_billing_types || [],
                              assigned_npis: insurance.assigned_npis || [],
                                      service_facility_npis: insurance.service_facility_npis || [],
                              authorization_exemptions: insurance.authorization_exemptions || [],
                              modifier_rules: insurance.modifier_rules || [],
                              secondary_routing_rules: insurance.secondary_routing_rules || [],
                              contact_info: insurance.contact_info || { phone: '', email: '', address: '' },
                              // Timely Filing Configuration
                              initial_filing_days: insurance.initial_filing_days || 90,
                              corrected_claim_days: insurance.corrected_claim_days || 90,
                              appeal_deadline_days: insurance.appeal_deadline_days || 60,
                              timely_filing_notes: insurance.timely_filing_notes || '',
                              // Payer Profile — Eligibility
                              rendering_npi_type: insurance.rendering_npi_type || 'organization',
                              requires_taxonomy: insurance.requires_taxonomy || false,
                              search_strategy: insurance.search_strategy || 'subscriber_dependent',
                              strip_member_id_suffix: insurance.strip_member_id_suffix !== false,
                              include_provider_address: insurance.include_provider_address !== false,
                              send_zip_plus4: insurance.send_zip_plus4 === true,
                              default_billing_entity_type: insurance.default_billing_entity_type || null,
                              box31_signature: insurance.box31_signature || null,
                              suppress_2310b_for_facility_billing: insurance.suppress_2310b_for_facility_billing === true,
                              suppress_service_facility_loop: insurance.suppress_service_facility_loop === true,
                              suppress_2310c_when_billing_address_matches: insurance.suppress_2310c_when_billing_address_matches === true,
                              suppress_2310c_npi_if_same_as_billing: insurance.suppress_2310c_npi_if_same_as_billing === true,
                              insurance_type_code: insurance.insurance_type_code || null,
                              loop_2310b_override: insurance.loop_2310b_override || null,
                              clearinghouse: insurance.clearinghouse || 'availity',
                              claims_clearinghouse: insurance.claims_clearinghouse || null,
                              trizetto_claims_payer_id: insurance.trizetto_claims_payer_id || '',
                              trizetto_eligibility_payer_id: insurance.trizetto_eligibility_payer_id || ''
                            });
                            setShowEditInsurance(true);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        {insurance.status !== 'archived' ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-amber-600 hover:bg-amber-50"
                            onClick={async () => {
                              if (window.confirm(`Archive "${insurance.name}"? It will be hidden from dropdowns but existing client records will be preserved.`)) {
                                try {
                                  await axios.patch(`${API}/insurances/${insurance.id}`, { status: 'archived' }, {
                                    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                  });
                                  toast.success('Insurance archived');
                                  fetchInsurances();
                                } catch (error) {
                                  toast.error('Failed to archive insurance');
                                }
                              }
                            }}
                          >
                            <Archive className="h-4 w-4 mr-1" />
                            Archive
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-green-600 hover:bg-green-50"
                            onClick={async () => {
                              try {
                                await axios.patch(`${API}/insurances/${insurance.id}`, { status: 'active' }, {
                                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                });
                                toast.success('Insurance restored');
                                fetchInsurances();
                              } catch (error) {
                                toast.error('Failed to restore insurance');
                              }
                            }}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 hover:bg-blue-50"
                          title="Migrate clients off this insurance"
                          onClick={() => setMigratingInsurance({ id: insurance.id, name: insurance.name })}
                        >
                          <ArrowRight className="h-4 w-4 mr-1" />
                          Migrate
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={async () => {
                            if (window.confirm(`Delete "${insurance.name}"? This can only be done if no claims have been billed under this insurance.`)) {
                              try {
                                await axios.delete(`${API}/insurances/${insurance.id}`, {
                                  headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                                });
                                toast.success('Insurance deleted');
                                fetchInsurances();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Cannot delete - insurance has billing history');
                              }
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
                
                {insuranceViewMode === 'grid' && (!insurances || insurances.length === 0) && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">No insurance companies configured</p>
                    <Button
                      onClick={() => setShowAddInsurance(true)}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Your First Insurance Company
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setShowManageInsurance(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
};

export const InsuranceFormDialogs = ({ ctx }) => {
  const {
    API,
    insuranceFormData,
    setInsuranceFormData,
    insurances,
    setInsurances,
    editingInsurance,
    setEditingInsurance,
    showAddInsurance,
    setShowAddInsurance,
    showEditInsurance,
    setShowEditInsurance,
    fetchInsurances,
    refreshNotes,
    refreshClients,
    refreshStats,
  } = ctx;

  const handleCreateInsurance = async (e) => {
    e.preventDefault();
    
    // Create a temporary ID for optimistic update
    const tempId = `temp-${Date.now()}`;
    const newInsurance = {
      ...insuranceFormData,
      id: tempId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: 'active'
    };
    
    // Optimistically add to list
    setInsurances(prev => [...prev, newInsurance]);
    
    // Close dialog and reset form immediately
    setShowAddInsurance(false);
    const formDataBackup = { ...insuranceFormData };
    setInsuranceFormData({
      name: '',
      abbreviation: '',
      payer_id: '',
      secondary_identifier: '',
      cob_use_carrier_code: false,
      plans: [],
      payer_billing_types: [],
      assigned_npis: [],
      service_facility_npis: [],
      authorization_exemptions: [],
      modifier_rules: [],
      secondary_routing_rules: [],
      contact_info: { phone: '', email: '', address: '' },
      rendering_npi_type: 'organization',
      requires_taxonomy: false,
      search_strategy: 'subscriber_dependent',
      strip_member_id_suffix: true,
    strip_member_id_prefix: '',
      include_provider_address: true,
      send_zip_plus4: false,
      default_billing_entity_type: null,
      box31_signature: null,
      suppress_2310b_for_facility_billing: false,
      suppress_service_facility_loop: false,
      suppress_2310c_when_billing_address_matches: false,
      suppress_2310c_npi_if_same_as_billing: false,
      insurance_type_code: null,
      loop_2310b_override: null
    });
    
    try {
      const response = await axios.post(`${API}/insurances`, formDataBackup);
      toast.success("Insurance created successfully");
      
      // Replace temp entry with real one from server
      setInsurances(prev => prev.map(ins => 
        ins.id === tempId ? response.data : ins
      ));
    } catch (error) {
      // Rollback - remove the temp entry
      setInsurances(prev => prev.filter(ins => ins.id !== tempId));
      
      // Handle Pydantic validation errors
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const errorMessages = error.response.data.detail.map(err => err.msg).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else {
        toast.error(error.response?.data?.detail || "Failed to create insurance");
      }
    }
  };

  // Update Insurance
  const handleUpdateInsurance = async (e) => {
    e.preventDefault();
    if (!editingInsurance) return;
    
    // Save previous state for rollback
    const previousInsurances = [...insurances];
    
    // Optimistically update the UI immediately
    const updatedInsurance = {
      ...editingInsurance,
      ...insuranceFormData,
      updated_at: new Date().toISOString()
    };
    
    setInsurances(prev => prev.map(ins => 
      ins.id === editingInsurance.id ? updatedInsurance : ins
    ));
    
    // Close the dialog immediately for better UX
    setShowEditInsurance(false);
    setEditingInsurance(null);
    
    try {
      await axios.put(`${API}/insurances/${editingInsurance.id}`, insuranceFormData);
      toast.success("Insurance updated successfully");
      // Refresh in background to get any server-side changes
      fetchInsurances();
    } catch (error) {
      // Rollback on error
      setInsurances(previousInsurances);
      
      // Handle Pydantic validation errors
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const errorMessages = error.response.data.detail.map(err => err.msg).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else {
        toast.error(error.response?.data?.detail || "Failed to update insurance");
      }
    }
  };

  return (
    <>
      {(() => {
        const renderInsuranceFormFields = (mode) => {
          const tid = mode === 'edit' ? 'edit-ins-' : 'ins-';
          return (
          <Accordion
            type="multiple"
            defaultValue={['basic', 'payer-ids']}
            className="w-full"
          >
            {/* ─────── Section 1: Basic Info & Contact (open by default) ─────── */}
            <AccordionItem value="basic">
              <AccordionTrigger className="font-semibold text-base">
                Basic Info & Contact
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div>
              <Label htmlFor={`${tid}name`}>Insurance Company Name *</Label>
              <DebouncedInput
                id={`${tid}name`}
                value={insuranceFormData.name}
                onChange={(e) => setInsuranceFormData({...insuranceFormData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor={`${tid}abbreviation`}>Abbreviation (for list view)</Label>
              <DebouncedInput
                id={`${tid}abbreviation`}
                value={insuranceFormData.abbreviation || ''}
                onChange={(e) => setInsuranceFormData({...insuranceFormData, abbreviation: e.target.value})}
                placeholder="e.g., BCBS, UHC, MCARE"
                maxLength={12}
              />
              <p className="text-xs text-gray-500 mt-1">Short name displayed in client lists (max 12 chars)</p>
            </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ─────── Section 2: Payer Settings (open by default) ─────── */}
            <AccordionItem value="payer-ids">
              <AccordionTrigger className="font-semibold text-base">
                Payer IDs, Clearinghouse Routing &amp; Contact
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div>
              <Label htmlFor={`${tid}payer-id`}>Payer ID *</Label>
              <DebouncedInput
                id={`${tid}payer-id`}
                value={insuranceFormData.payer_id}
                onChange={(e) => setInsuranceFormData({...insuranceFormData, payer_id: e.target.value})}
                required
                placeholder="UHC001"
              />
            </div>
            <div>
              <Label htmlFor={`${tid}elig-payer-id`}>Eligibility Payer ID</Label>
              <DebouncedInput
                id={`${tid}elig-payer-id`}
                value={insuranceFormData.eligibility_payer_id || ''}
                onChange={(e) => setInsuranceFormData({...insuranceFormData, eligibility_payer_id: e.target.value})}
                placeholder="Overrides Payer ID for 270/271"
              />
              <p className="text-xs text-gray-500 mt-1">Used for eligibility checks. Falls back to Payer ID if blank.</p>
            </div>
            <div>
              <Label htmlFor={`${tid}claims-payer-id`}>Claims Payer ID</Label>
              <DebouncedInput
                id={`${tid}claims-payer-id`}
                value={insuranceFormData.claims_payer_id || ''}
                onChange={(e) => setInsuranceFormData({...insuranceFormData, claims_payer_id: e.target.value})}
                placeholder="Overrides Payer ID for 837P"
              />
            </div>
            <div>
              <Label htmlFor={`${tid}era-payer-id`}>ERA Payer ID</Label>
              <DebouncedInput
                id={`${tid}era-payer-id`}
                value={insuranceFormData.era_payer_id || ''}
                onChange={(e) => setInsuranceFormData({...insuranceFormData, era_payer_id: e.target.value})}
                placeholder="Overrides Payer ID for 835"
              />
            </div>
            <div>
              <Label htmlFor={`${tid}clearinghouse`}>Clearinghouse</Label>
              <select
                id={`${tid}clearinghouse`}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={insuranceFormData.clearinghouse || 'availity'}
                onChange={(e) => setInsuranceFormData({...insuranceFormData, clearinghouse: e.target.value})}
                data-testid={`${tid}clearinghouse-select`}
              >
                <option value="availity">Availity (SFTP)</option>
                <option value="nehen">NEHEN (NEHEN003)</option>
                <option value="optum">Optum / Change Healthcare</option>
                <option value="direct">Direct / Portal</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Determines which clearinghouse routes 270/271, 837P, and 835 for this payer.</p>
            </div>
            <div>
              <Label htmlFor={`${tid}claims-clearinghouse`}>Claims Clearinghouse Override <span className="text-gray-400 font-normal">(optional)</span></Label>
              <select
                id={`${tid}claims-clearinghouse`}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={insuranceFormData.claims_clearinghouse || ''}
                onChange={(e) => setInsuranceFormData({...insuranceFormData, claims_clearinghouse: e.target.value || null})}
                data-testid={`${tid}claims-clearinghouse-select`}
              >
                <option value="">Use main clearinghouse (default)</option>
                <option value="availity">Availity</option>
                <option value="nehen">NEHEN / TriZetto</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Overrides the clearinghouse for <strong>837P claims only</strong> — leave blank when claims and eligibility share a clearinghouse.
                Use this when a payer routes <em>eligibility</em> via NEHEN but the <em>claims</em> contract pays through Availity (or vice versa).
              </p>
            </div>
            <div>
              <Label htmlFor={`${tid}insurance-type-code`}>Insurance Type Code (SBR09) <span className="text-gray-400 font-normal">(optional)</span></Label>
              <select
                id={`${tid}insurance-type-code`}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={insuranceFormData.insurance_type_code || ''}
                onChange={(e) => setInsuranceFormData({...insuranceFormData, insurance_type_code: e.target.value || null})}
                data-testid={`${tid}insurance-type-code-select`}
              >
                <option value="">Auto (CI default; MC for Medicaid name match)</option>
                <option value="CI">CI — Commercial Insurance Co.</option>
                <option value="HM">HM — Health Maintenance Organization (HMO)</option>
                <option value="MA">MA — Medicare Part A</option>
                <option value="MB">MB — Medicare Part B</option>
                <option value="MC">MC — Medicaid</option>
                <option value="BL">BL — Blue Cross / Blue Shield</option>
                <option value="12">12 — Preferred Provider Organization (PPO)</option>
                <option value="13">13 — Point of Service (POS)</option>
                <option value="14">14 — Exclusive Provider Organization (EPO)</option>
                <option value="15">15 — Indemnity Insurance</option>
                <option value="16">16 — HMO Medicare Risk</option>
                <option value="11">11 — Other Non-Federal Programs</option>
                <option value="09">09 — Self-pay</option>
                <option value="CH">CH — CHAMPUS / TRICARE</option>
                <option value="VA">VA — Veterans Affairs Plan</option>
                <option value="WC">WC — Workers' Compensation</option>
                <option value="DS">DS — Disability</option>
                <option value="LM">LM — Liability Medical</option>
                <option value="AM">AM — Automobile Medical</option>
                <option value="OF">OF — Other Federal Program</option>
                <option value="FI">FI — Federal Employees Program</option>
                <option value="TV">TV — Title V</option>
                <option value="ZZ">ZZ — Mutually Defined</option>
                <option value="17">17 — Dental Maintenance Organization</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                X12 837P SBR09 — overrides the auto-detected Claim Filing Indicator. Required for correct routing of Medicare (MA/MB),
                HMOs (HM), BCBS (BL), PPO/POS plans, and Workers Comp — wrong code triggers 999 IK3 acks at most clearinghouses.
              </p>
            </div>
            {/* TriZetto-specific payer ID overrides — only when this insurance routes through NEHEN/TriZetto. */}
            {(insuranceFormData.clearinghouse === 'nehen') && (
              <>
                <div className="md:col-span-2 -mt-2">
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <strong>TriZetto Provider Solutions</strong> uses internal payer IDs that may differ from CMS standard IDs.
                    Set these <em>only if</em> TriZetto explicitly assigned a different ID for this payer.
                    Leave blank to let TriZetto auto-route from the standard payer ID.
                  </div>
                </div>
                <div>
                  <Label htmlFor={`${tid}trizetto-claims-id`}>TriZetto Claims Payer ID</Label>
                  <DebouncedInput
                    id={`${tid}trizetto-claims-id`}
                    value={insuranceFormData.trizetto_claims_payer_id || ''}
                    onChange={(e) => setInsuranceFormData({...insuranceFormData, trizetto_claims_payer_id: e.target.value})}
                    placeholder="TriZetto's ID for 837P routing"
                    data-testid={`${tid}trizetto-claims-id`}
                  />
                  <p className="text-xs text-gray-500 mt-1">Stamped into NM1*PR for 837P claims when this carrier routes through NEHEN.</p>
                </div>
                <div>
                  <Label htmlFor={`${tid}trizetto-elig-id`}>TriZetto Eligibility Payer ID (GediPayerID)</Label>
                  <DebouncedInput
                    id={`${tid}trizetto-elig-id`}
                    value={insuranceFormData.trizetto_eligibility_payer_id || ''}
                    onChange={(e) => setInsuranceFormData({...insuranceFormData, trizetto_eligibility_payer_id: e.target.value})}
                    placeholder="TriZetto's ID for 270/271"
                    data-testid={`${tid}trizetto-elig-id`}
                  />
                  <p className="text-xs text-gray-500 mt-1">Used for the SOAP <code>GediPayerID</code> element + the 270's NM1*PR segment.</p>
                </div>
              </>
            )}
            <div className="md:col-span-2">
              <Label htmlFor={`${tid}secondary-id`}>Secondary Identifier (optional)</Label>
              <DebouncedInput
                id={`${tid}secondary-id`}
                value={insuranceFormData.secondary_identifier || ''}
                onChange={(e) => setInsuranceFormData({...insuranceFormData, secondary_identifier: e.target.value})}
                placeholder="e.g., 0092000 for Harvard Pilgrim MassHealth, 0085000 for Medicare"
              />
              <p className="text-xs text-gray-500 mt-1">Secondary ID for billing (e.g., MassHealth requires this for primary claim results)</p>
              {/* Jun 2026 — MassHealth-style COB carrier codes */}
              <label className="flex items-center space-x-2 mt-3">
                <input
                  type="checkbox"
                  data-testid={`${tid}cob-carrier-code`}
                  checked={!!insuranceFormData.cob_use_carrier_code}
                  onChange={(e) => setInsuranceFormData({...insuranceFormData, cob_use_carrier_code: e.target.checked})}
                />
                <span className="text-sm font-medium">COB: identify other payers by carrier code (Loop 2330B NM109 / 2430 SVD01)</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Turn ON for payers like MassHealth that require THEIR assigned carrier code for the other insurance on secondary/tertiary claims.
                When this payer is billed as secondary/tertiary, each other payer&apos;s Secondary Identifier (its carrier code) goes in Loop 2330B NM109
                and is repeated in Loop 2430 SVD01 (and CMS-1500 Box 9d) instead of the standard payer ID. Falls back to the standard payer ID
                with a claim-preview warning when the other payer has no Secondary Identifier on file.
              </p>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor={`${tid}plans`}>Plans (comma-separated, optional)</Label>
              <DebouncedInput
                id={`${tid}plans`}
                value={(insuranceFormData.plans || []).map(p => {
                  if (typeof p === 'string') return p;
                  return p.secondary_identifier ? `${p.name}[${p.secondary_identifier}]` : p.name;
                }).join(', ')}
                onChange={(e) => {
                  const planStrings = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                  const plans = planStrings.map(planStr => {
                    if (planStr.includes('[') && planStr.includes(']')) {
                      const name = planStr.substring(0, planStr.indexOf('[')).trim();
                      const secId = planStr.substring(planStr.indexOf('[') + 1, planStr.indexOf(']')).trim();
                      return { name, secondary_identifier: secId || null };
                    }
                    return { name: planStr, secondary_identifier: null };
                  });
                  setInsuranceFormData({...insuranceFormData, plans});
                }}
                placeholder="HMO Blue, PPO Blue[0092001], Medicare Advantage[0092002]"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter plan names separated by commas. Add plan-specific secondary ID in brackets: "Plan Name[ID]". 
                Plans without brackets will use the company-level secondary identifier above.
              </p>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor={`${tid}billing-types`}>Payer Billing Types</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {['facility', 'individual', 'supervisory', 'medicare', 'medicaid', 'group'].map(type => (
                  <label key={type} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={insuranceFormData.payer_billing_types.includes(type)}
                      onChange={(e) => {
                        const types = insuranceFormData.payer_billing_types;
                        if (e.target.checked) {
                          setInsuranceFormData({...insuranceFormData, payer_billing_types: [...types, type]});
                        } else {
                          setInsuranceFormData({...insuranceFormData, payer_billing_types: types.filter(t => t !== type)});
                        }
                      }}
                    />
                    <span className="text-sm capitalize">{type}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor={`${tid}phone`}>Contact Phone</Label>
              <DebouncedInput
                id={`${tid}phone`}
                value={insuranceFormData.contact_info.phone}
                onChange={(e) => setInsuranceFormData({
                  ...insuranceFormData,
                  contact_info: {...insuranceFormData.contact_info, phone: e.target.value}
                })}
                placeholder="(800) 555-0123"
              />
            </div>
            <div>
              <Label htmlFor={`${tid}email`}>Contact Email</Label>
              <DebouncedInput
                id={`${tid}email`}
                type="email"
                value={insuranceFormData.contact_info.email}
                onChange={(e) => setInsuranceFormData({
                  ...insuranceFormData,
                  contact_info: {...insuranceFormData.contact_info, email: e.target.value}
                })}
                placeholder="provider@insurance.com"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor={`${tid}address`}>Contact Address</Label>
              <DebouncedInput
                id={`${tid}address`}
                value={insuranceFormData.contact_info.address}
                onChange={(e) => setInsuranceFormData({
                  ...insuranceFormData,
                  contact_info: {...insuranceFormData.contact_info, address: e.target.value}
                })}
                placeholder="123 Insurance Ave, City, State 12345"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor={`${tid}npis`}>Assigned NPI Numbers (comma-separated)</Label>
              <DebouncedInput
                id={`${tid}npis`}
                value={insuranceFormData.assigned_npis.join(', ')}
                onChange={(e) => setInsuranceFormData({
                  ...insuranceFormData,
                  assigned_npis: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                })}
                placeholder="1234567890, 0987654321"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor={`${tid}sf-npis`}>Service Facility NPI Override (comma-separated)</Label>
              <DebouncedInput
                id={`${tid}sf-npis`}
                value={(insuranceFormData.service_facility_npis || []).join(', ')}
                onChange={(e) => setInsuranceFormData({
                  ...insuranceFormData,
                  service_facility_npis: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                })}
                placeholder="e.g., 1487863494"
                data-testid="insurance-service-facility-npis"
              />
              <p className="text-xs text-gray-500 mt-1">
                When set, the first NPI is used in claim Loop 2310C (Service
                Facility Location) for this payer, overriding the location&apos;s
                own NPI — same pattern as the billing NPI override above.
              </p>
            </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ─────── Section 3: Payer Profile ─────── */}
            {/* Feb 2026 — renamed from "Eligibility (270) Payer
                Profile" to just "Payer Profile" because the scope
                now spans both eligibility AND claim formatting
                (Box 31 signature, default billing entity,
                2310B/2310C suppression, SBR09 override, etc.). */}
            <AccordionItem value="eligibility-270">
              <AccordionTrigger className="font-semibold text-base">
                <span className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  Payer Profile
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pt-2">
                  <p className="text-sm text-gray-600 mb-3">
                    Per-payer overrides covering both <strong>Eligibility (270)</strong> inquiries and <strong>Claim (837P / CMS-1500)</strong> generation: search strategy, rendering NPI type, Box 31 signature, default billing entity (Box 33), Loop 2310B/2310C handling, SBR09 type code, member ID transforms, and more. Incorrect settings cause 270 AAA rejections or claim payments under the wrong NPI.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor={`${tid}npi-type`}>NPI Type</Label>
                  <select
                    id={`${tid}npi-type`}
                    data-testid={`${tid}rendering-npi-type`}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                    value={insuranceFormData.rendering_npi_type || 'organization'}
                    onChange={(e) => setInsuranceFormData({...insuranceFormData, rendering_npi_type: e.target.value})}
                  >
                    <option value="organization">Organization (NM1*1P*2)</option>
                    <option value="individual">Individual / Rendering (NM1*1P*1)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Individual = provider NPI, Organization = group NPI</p>
                </div>
                <div>
                  <Label htmlFor={`${tid}search-strategy`}>Search Strategy</Label>
                  <select
                    id={`${tid}search-strategy`}
                    data-testid={`${tid}search-strategy`}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                    value={insuranceFormData.search_strategy || 'subscriber_dependent'}
                    onChange={(e) => setInsuranceFormData({...insuranceFormData, search_strategy: e.target.value})}
                  >
                    <option value="subscriber_dependent">Subscriber + Dependent (standard)</option>
                    <option value="subscriber_only">Subscriber Only (skip Loop 2000D)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">CCA requires subscriber_only to avoid AAA 79</p>
                </div>
                <div className="flex items-center pt-5">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`${tid}requires-taxonomy`}
                      checked={insuranceFormData.requires_taxonomy || false}
                      onChange={(e) => setInsuranceFormData({...insuranceFormData, requires_taxonomy: e.target.checked})}
                    />
                    <span className="text-sm">Requires Taxonomy (PRV segment)</span>
                  </label>
                </div>
              </div>
              <div className="mt-3">
                <label className="flex items-start space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`${tid}strip-member-id-suffix`}
                    className="mt-1"
                    checked={insuranceFormData.strip_member_id_suffix !== false}
                    onChange={(e) => setInsuranceFormData({...insuranceFormData, strip_member_id_suffix: e.target.checked})}
                  />
                  <span className="text-sm">
                    <span className="font-medium">Strip dependent suffix from Member ID</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Default ON — strips trailing <code>-01</code>/<code>-02</code> dependent codes from member IDs (required by CCA, Tufts SCO, BMC HealthNet, most MassHealth managed care).
                      <strong className="text-amber-700"> Turn OFF for Harvard Pilgrim</strong> and any payer that needs the full member ID *with* the dependent suffix to identify the family member (else returns AAA*72 "Invalid/Missing Patient Identification").
                    </span>
                  </span>
                </label>
              </div>
              <div className="mt-3">
                <label className="flex items-start space-x-2">
                  <span className="text-sm flex-1">
                    <span className="font-medium">Strip leading prefix from Member ID (Claims only)</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Optional — strips this exact prefix (case-insensitive) from the member ID before it's sent in NM109 of <strong>837P claims only</strong>. Eligibility (270) submissions keep the prefix intact.
                      <strong className="text-amber-700"> Set to <code>N</code> for NEHEN-Tufts</strong> — that payer accepts the leading "N" for eligibility but rejects it on claim submissions.
                      Leave blank for every other payer.
                    </span>
                  </span>
                  <input
                    type="text"
                    data-testid={`${tid}strip-member-id-prefix`}
                    className="mt-1 px-2 py-1 border rounded w-24 text-sm uppercase font-mono"
                    placeholder="e.g. N"
                    maxLength={6}
                    value={insuranceFormData.strip_member_id_prefix || ''}
                    onChange={(e) => setInsuranceFormData({...insuranceFormData, strip_member_id_prefix: e.target.value.toUpperCase()})}
                  />
                </label>
              </div>
              <div className="mt-3">
                <label className="flex items-start space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`${tid}include-provider-address`}
                    className="mt-1"
                    checked={insuranceFormData.include_provider_address !== false}
                    onChange={(e) => setInsuranceFormData({...insuranceFormData, include_provider_address: e.target.checked})}
                  />
                  <span className="text-sm">
                    <span className="font-medium">Include provider address (N3/N4) in 270</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Default ON — sends the practice address in Loop 2100B (required by Medicare CEDI to avoid AAA*50 "Invalid/Missing Provider ZIP Code").
                      <strong className="text-amber-700"> Turn OFF for Harvard Pilgrim</strong> and any payer whose CAQH CORE implementation rejects 270s containing the provider address (returns 999 <code>IK3*N4*14*2100*1</code> "Unrecognized or disallowed segment"). These payers match providers by NPI alone.
                    </span>
                  </span>
                </label>
              </div>
              <div className="mt-3">
                <label className="flex items-start space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`${tid}send-zip-plus4`}
                    className="mt-1"
                    checked={insuranceFormData.send_zip_plus4 === true}
                    onChange={(e) => setInsuranceFormData({...insuranceFormData, send_zip_plus4: e.target.checked})}
                  />
                  <span className="text-sm">
                    <span className="font-medium">Send 9-digit ZIP+4 in N403 of the 270</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Default OFF — emits the 5-digit ZIP only, which most CAQH CORE Phase II payers (Harvard Pilgrim, Tufts SCO, BCBS plans) accept.
                      <strong className="text-amber-700"> Turn ON for Medicare CEDI</strong> and any other payer that requires the full ZIP+4 (PECOS-keyed payers reject 5-digit with AAA*50 "Invalid/Missing Provider ZIP Code"). The org must have a 9-digit ZIP saved in Admin → Setup → Organization, otherwise the 270 will hard-fail with a clear error.
                    </span>
                  </span>
                </label>
              </div>

              {/* Claim Formatting (Advanced) — May 2026.  Drives who appears
                  in CMS-1500 Box 33 / Loop 2010AA NM1*85.  Backend: 
                  services/billing_entity_resolver.py + 
                  routes/billing.py::create_claim. */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <h5 className="font-medium text-gray-800 mb-1 text-sm flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-blue-600" />
                  Claim Formatting (Advanced)
                </h5>
                <div className="mt-2">
                  <label className="block">
                    <span className="text-sm font-medium">Default billing entity (CMS-1500 Box 33)</span>
                    <select
                      data-testid={`${tid}default-billing-entity-type`}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                      value={insuranceFormData.default_billing_entity_type || ''}
                      onChange={(e) => setInsuranceFormData({
                        ...insuranceFormData,
                        default_billing_entity_type: e.target.value || null,
                      })}
                    >
                      <option value="">Use legacy routing (default)</option>
                      <option value="facility">Facility / Clinic NPI in Box 33</option>
                      <option value="provider">Rendering Provider NPI in Box 33</option>
                      <option value="supervisor">Supervising Provider NPI in Box 33</option>
                    </select>
                    <span className="block text-xs text-gray-500 mt-1">
                      <strong>Default: Legacy routing</strong> — falls back to <code>payer_billing_types</code> + provider credential rules (existing behavior, byte-identical for all unset payers).
                      <strong className="text-amber-700"> Set to "Facility / Clinic NPI"</strong> for almost all private-practice payers — places the clinic's organization NPI in Box 33 (Loop 2010AA NM1*85) so the clinic gets paid, while the rendering provider stays in Box 24J.
                      Interns ALWAYS bill under their supervisor regardless of this setting (regulatory).
                    </span>
                  </label>
                </div>
                <div className="mt-3">
                  <label className="block">
                    <span className="text-sm font-medium">Box 31 signature (CMS-1500)</span>
                    <select
                      data-testid={`${tid}box31-signature`}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                      value={insuranceFormData.box31_signature || ''}
                      onChange={(e) => setInsuranceFormData({
                        ...insuranceFormData,
                        box31_signature: e.target.value || null,
                      })}
                    >
                      <option value="">Auto — match billing entity (default)</option>
                      <option value="facility">Always show Facility / Clinic name</option>
                      <option value="rendering_provider">Always show Rendering Provider name + credentials</option>
                    </select>
                    <span className="block text-xs text-gray-500 mt-1">
                      Some payers (e.g., Medicare CEDI, certain BCBS plans) require the rendering provider's name + credentials in Box 31 even when the claim is billed as a facility — others want the clinic legal name. <strong>Auto</strong> uses the facility name for facility billing and the rendering provider otherwise. Override only when a specific payer rejects claims for the wrong Box 31 content.
                    </span>
                  </label>
                </div>
                {/* Feb 2026 — Per-payer toggle to suppress 2310B
                    Rendering Provider loop on facility-billed claims.
                    Tufts (and a few others) read NM1*82 in 2310B as the
                    credentialing payee, paying the individual provider
                    instead of the org NPI in 2010AA.  Default OFF
                    keeps every other payer byte-identical. */}
                <div className="mt-3 p-3 border rounded-md bg-rose-50/40">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      data-testid={`${tid}suppress-2310b-facility`}
                      checked={!!insuranceFormData.suppress_2310b_for_facility_billing}
                      onChange={(e) => setInsuranceFormData({
                        ...insuranceFormData,
                        suppress_2310b_for_facility_billing: e.target.checked,
                      })}
                    />
                    <span className="text-sm font-medium">Suppress Loop 2310B (Rendering Provider) on facility-billed claims</span>
                  </label>
                  <p className="text-xs text-gray-600 mt-1 ml-6">
                    Enable for payers that read NM1*82 in Loop 2310B as the payee for credentialing (Tufts is the canonical example).  When ON and the claim is facility-billed under this payer (non-Medicare), the 837P generator omits the entire 2310B Rendering Provider loop so the org NPI in 2010AA is the unambiguous payee.  Medicare claims continue to emit 2310B regardless.  <strong>Default OFF</strong> — most commercial payers (BCBS, Aetna, Cigna) treat 2310B as informational, so leave OFF unless a specific payer pays under the individual provider despite facility billing.
                  </p>
                </div>
                {/* Loop 2310B Rendering Provider override (BCBS-style org-on-2310B).
                    Per-payer escape hatch for payers that require a specific
                    name + NPI + entity qualifier on the rendering loop that
                    differs from both the billing provider (2010AA) and the
                    service facility (2310C). */}
                <div className="mt-3 p-3 border rounded-md bg-amber-50/50">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      data-testid={`${tid}loop-2310b-override-enabled`}
                      checked={!!(insuranceFormData.loop_2310b_override?.enabled)}
                      onChange={(e) => setInsuranceFormData({
                        ...insuranceFormData,
                        loop_2310b_override: e.target.checked
                          ? { ...(insuranceFormData.loop_2310b_override || {}), enabled: true, entity_qualifier: insuranceFormData.loop_2310b_override?.entity_qualifier || '2' }
                          : { ...(insuranceFormData.loop_2310b_override || {}), enabled: false },
                      })}
                    />
                    <span className="text-sm font-medium">Override Loop 2310B (Rendering Provider) for this payer</span>
                  </label>
                  <p className="text-xs text-gray-600 mt-1 ml-6">
                    Some payers (notably BCBS) require the organization (not the rendering individual) on Loop 2310B with a different NPI than the one on 2010AA / 2310C. When enabled, the values below REPLACE the default 2310B emission for this payer.
                  </p>
                  {insuranceFormData.loop_2310b_override?.enabled && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 ml-6">
                      <label className="block">
                        <span className="text-xs font-medium">Entity qualifier</span>
                        <select
                          data-testid={`${tid}loop-2310b-qualifier`}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                          value={insuranceFormData.loop_2310b_override?.entity_qualifier || '2'}
                          onChange={(e) => setInsuranceFormData({
                            ...insuranceFormData,
                            loop_2310b_override: { ...(insuranceFormData.loop_2310b_override || {}), entity_qualifier: e.target.value },
                          })}
                        >
                          <option value="2">2 — Non-Person / Organization (BCBS style)</option>
                          <option value="1">1 — Person</option>
                        </select>
                      </label>
                      <label className="block">
                        <span className="text-xs font-medium">NPI (Type-II / org or individual)</span>
                        <input
                          type="text"
                          data-testid={`${tid}loop-2310b-npi`}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                          placeholder="1134578719"
                          value={insuranceFormData.loop_2310b_override?.npi || ''}
                          onChange={(e) => setInsuranceFormData({
                            ...insuranceFormData,
                            loop_2310b_override: { ...(insuranceFormData.loop_2310b_override || {}), npi: e.target.value.trim() },
                          })}
                        />
                      </label>
                      <label className="block md:col-span-2">
                        <span className="text-xs font-medium">
                          {insuranceFormData.loop_2310b_override?.entity_qualifier === '1' ? 'Last name' : 'Organization name'}
                        </span>
                        <input
                          type="text"
                          data-testid={`${tid}loop-2310b-last-name`}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                          placeholder="COUNSELING ASSESSMENT CLINIC WORCESTER"
                          value={insuranceFormData.loop_2310b_override?.last_name || ''}
                          onChange={(e) => setInsuranceFormData({
                            ...insuranceFormData,
                            loop_2310b_override: { ...(insuranceFormData.loop_2310b_override || {}), last_name: e.target.value },
                          })}
                        />
                      </label>
                      {insuranceFormData.loop_2310b_override?.entity_qualifier === '1' && (
                        <>
                          <label className="block">
                            <span className="text-xs font-medium">First name</span>
                            <input
                              type="text"
                              data-testid={`${tid}loop-2310b-first-name`}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                              value={insuranceFormData.loop_2310b_override?.first_name || ''}
                              onChange={(e) => setInsuranceFormData({
                                ...insuranceFormData,
                                loop_2310b_override: { ...(insuranceFormData.loop_2310b_override || {}), first_name: e.target.value },
                              })}
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs font-medium">Middle initial (optional)</span>
                            <input
                              type="text"
                              maxLength={1}
                              data-testid={`${tid}loop-2310b-middle-name`}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                              value={insuranceFormData.loop_2310b_override?.middle_name || ''}
                              onChange={(e) => setInsuranceFormData({
                                ...insuranceFormData,
                                loop_2310b_override: { ...(insuranceFormData.loop_2310b_override || {}), middle_name: e.target.value },
                              })}
                            />
                          </label>
                        </>
                      )}
                      <label className="block md:col-span-2">
                        <span className="text-xs font-medium">Taxonomy code (optional — falls back to org taxonomy)</span>
                        <input
                          type="text"
                          data-testid={`${tid}loop-2310b-taxonomy`}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                          placeholder="251S00000X"
                          value={insuranceFormData.loop_2310b_override?.taxonomy || ''}
                          onChange={(e) => setInsuranceFormData({
                            ...insuranceFormData,
                            loop_2310b_override: { ...(insuranceFormData.loop_2310b_override || {}), taxonomy: e.target.value.trim() },
                          })}
                        />
                      </label>
                      <div className="md:col-span-2 text-xs text-gray-600">
                        <strong>Preview:</strong>{' '}
                        <code className="text-amber-800">
                          NM1*82*{insuranceFormData.loop_2310b_override?.entity_qualifier || '2'}*{(insuranceFormData.loop_2310b_override?.last_name || '').toUpperCase() || '<NAME>'}*{(insuranceFormData.loop_2310b_override?.first_name || '').toUpperCase()}*{(insuranceFormData.loop_2310b_override?.middle_name || '').toUpperCase()}***XX*{insuranceFormData.loop_2310b_override?.npi || '<NPI>'}
                        </code>
                      </div>
                      {/* Feb 20 2026 — surface missing-required-field warnings
                          BEFORE save so the user catches the BCBS config error
                          (where the EDI fell back to the provider name + provider
                          taxonomy when the override fields were blank). */}
                      {(() => {
                        const ovr = insuranceFormData.loop_2310b_override || {};
                        const missing = [];
                        if (!(ovr.last_name || '').trim()) {
                          missing.push(ovr.entity_qualifier === '1' ? 'Last name' : 'Organization name');
                        }
                        if (!(ovr.npi || '').trim()) missing.push('NPI');
                        if (missing.length === 0) return null;
                        return (
                          <div className="md:col-span-2 text-xs px-2 py-1.5 rounded border border-red-300 bg-red-50 text-red-800" data-testid="loop-2310b-override-warning">
                            <strong>⚠ Missing required override fields:</strong> {missing.join(', ')}. The EDI will emit blank values for these segments until they are filled in.
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
                {/* Jun 2026 — Per-payer toggle to suppress the Service
                    Facility loop (2310C/2310D) entirely on outbound EDI
                    claims, regardless of whether the service location
                    differs from the billing address.  Placed AFTER the
                    two Rendering Provider (2310B) controls so related
                    toggles stay grouped (user request). */}
                <div className="mt-3 p-3 border rounded-md bg-sky-50/40">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      data-testid={`${tid}suppress-service-facility-loop`}
                      checked={!!insuranceFormData.suppress_service_facility_loop}
                      onChange={(e) => setInsuranceFormData({
                        ...insuranceFormData,
                        suppress_service_facility_loop: e.target.checked,
                      })}
                    />
                    <span className="text-sm font-medium">Suppress Service Facility loop (2310C/2310D) on EDI claims</span>
                  </label>
                  <p className="text-xs text-gray-600 mt-1 ml-6">
                    When ON, the service facility segment (NM1*77 + address + secondary ID) is omitted from <strong>all</strong> 837P claims sent to this payer — even when the service location differs from the billing address.  Use for payers that reject or mis-route claims containing a service facility loop.  <strong>Default OFF.</strong>
                  </p>
                </div>
                {/* Jun 2026 — EDI 837P Advanced Formatting: conditional
                    2310C suppressions per the BCBSMA companion guide.
                    Both default OFF so existing claims are unchanged. */}
                <div className="mt-3 p-3 border rounded-md bg-indigo-50/40 space-y-3">
                  <div className="text-sm font-semibold text-indigo-900">EDI 837P Advanced Formatting — Service Facility (Loop 2310C)</div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        data-testid={`${tid}suppress-2310c-addr-match`}
                        checked={!!insuranceFormData.suppress_2310c_when_billing_address_matches}
                        onChange={(e) => setInsuranceFormData({
                          ...insuranceFormData,
                          suppress_2310c_when_billing_address_matches: e.target.checked,
                        })}
                      />
                      <span className="text-sm font-medium">Omit Service Facility (Loop 2310C) if Service Address matches Billing Address</span>
                    </label>
                    <p className="text-xs text-gray-600 mt-1 ml-6">
                      Standard ANSI 5010 behavior — 2310C is only meant to be sent when the place of service differs from the billing address (Loop 2010AA).  Addresses are compared after normalizing case, punctuation and common abbreviations (St/Street, Ste/Suite…) using ZIP-5.  <strong>Default OFF</strong> — claims stay exactly as they are today until you enable this.
                    </p>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        data-testid={`${tid}suppress-2310c-dup-npi`}
                        checked={!!insuranceFormData.suppress_2310c_npi_if_same_as_billing}
                        onChange={(e) => setInsuranceFormData({
                          ...insuranceFormData,
                          suppress_2310c_npi_if_same_as_billing: e.target.checked,
                        })}
                      />
                      <span className="text-sm font-medium">Suppress Service Facility NPI (NM108/NM109) if it matches Billing NPI</span>
                    </label>
                    <p className="text-xs text-gray-600 mt-1 ml-6">
                      Some payers (e.g., <strong>BCBS MA</strong>) reject claims if the Service Facility NPI duplicates the Billing NPI in Loop 2010AA.  When ON and the NPIs match, the 2310C name/address still go out but without NM108/NM109 (and without the facility REF).  Uses the payer-resolved NPIs that actually appear on the claim.  <strong>Default OFF.</strong>
                    </p>
                  </div>
                </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ─────── Section 4: Authorization Exemptions ─────── */}
            <AccordionItem value="auth-exemptions">
              <AccordionTrigger className="font-semibold text-base">
                <span className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-green-600" />
                  Authorization Exemptions
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pt-2">
                  <p className="text-sm text-gray-600 mb-3">
                    CPT codes that do NOT require authorization for this insurance, even if the CPT code is marked as "Requires Authorization".
                    Example: CBHI codes for United.
                  </p>
                  <div>
                    <Label htmlFor={`${tid}auth-exemptions`}>Exempt CPT Codes (comma-separated)</Label>
                    <DebouncedInput
                      id={`${tid}auth-exemptions`}
                      value={(insuranceFormData.authorization_exemptions || []).join(', ')}
                      onChange={(e) => setInsuranceFormData({
                        ...insuranceFormData,
                        authorization_exemptions: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                      })}
                      placeholder="H2019, H0032, H0036, 90847"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter CPT codes that this insurance does not require authorization for
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ─────── Section 5: Per-Payer Modifier Overrides ─────── */}
            <AccordionItem value="modifier-overrides">
              <AccordionTrigger className="font-semibold text-base">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-indigo-600" />
                  Modifier Overrides
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pt-2">
                  <p className="text-sm text-gray-600 mb-3">
                    Replace the auto-inferred credential modifier for this payer. Example: BCBS wants{' '}
                    <code className="font-mono bg-white px-1 rounded">SA</code> billed as{' '}
                    <code className="font-mono bg-white px-1 rounded">HO</code> for psychotherapy
                    codes. Leave <em>Limit to CPT codes</em> blank to apply to every CPT.
                  </p>
                  <PayerModifierRulesEditor
                    rules={insuranceFormData.modifier_rules || []}
                    onChange={(next) => setInsuranceFormData({ ...insuranceFormData, modifier_rules: next })}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ─────── Section 5b: Secondary Routing Rules ─────── */}
            <AccordionItem value="secondary-routing">
              <AccordionTrigger className="font-semibold text-base">
                <span className="flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-emerald-600" />
                  Secondary Routing Rules
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pt-2">
                  <p className="text-sm text-gray-600 mb-3">
                    When a primary EOB posts and <strong>this payer is the secondary</strong> on the
                    claim, the billing engine automatically applies the first matching rule below —
                    no manual routing needed. Skipped balances go to the next payer in sequence
                    when one exists; otherwise to the client account or a write-off, per the rule.
                  </p>
                  <SecondaryRoutingRulesEditor
                    rules={insuranceFormData.secondary_routing_rules || []}
                    onChange={(next) => setInsuranceFormData({ ...insuranceFormData, secondary_routing_rules: next })}
                    payers={(insurances || []).filter((p) => p.id !== editingInsurance?.id)}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* ─────── Section 6: Timely Filing Limits ─────── */}
            <AccordionItem value="timely-filing">
              <AccordionTrigger className="font-semibold text-base">
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  Timely Filing Limits
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pt-2">
                  <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor={`${tid}initial-filing`}>Initial Filing (days)</Label>
                  <Input
                    id={`${tid}initial-filing`}
                    type="number"
                    min="1"
                    max="365"
                    value={insuranceFormData.initial_filing_days || 90}
                    onChange={(e) => setInsuranceFormData({
                      ...insuranceFormData,
                      initial_filing_days: parseInt(e.target.value) || 90
                    })}
                    placeholder="90"
                  />
                  <p className="text-xs text-gray-500 mt-1">Days from DOS to submit</p>
                </div>
                <div>
                  <Label htmlFor={`${tid}corrected-claim`}>Corrected Claim (days)</Label>
                  <Input
                    id={`${tid}corrected-claim`}
                    type="number"
                    min="1"
                    max="365"
                    value={insuranceFormData.corrected_claim_days || 90}
                    onChange={(e) => setInsuranceFormData({
                      ...insuranceFormData,
                      corrected_claim_days: parseInt(e.target.value) || 90
                    })}
                    placeholder="90"
                  />
                  <p className="text-xs text-gray-500 mt-1">Days from denial to resubmit</p>
                </div>
                <div>
                  <Label htmlFor={`${tid}appeal-deadline`}>Appeal Deadline (days)</Label>
                  <Input
                    id={`${tid}appeal-deadline`}
                    type="number"
                    min="1"
                    max="365"
                    value={insuranceFormData.appeal_deadline_days || 60}
                    onChange={(e) => setInsuranceFormData({
                      ...insuranceFormData,
                      appeal_deadline_days: parseInt(e.target.value) || 60
                    })}
                    placeholder="60"
                  />
                  <p className="text-xs text-gray-500 mt-1">Days from denial to appeal</p>
                </div>
              </div>
              <div className="mt-3">
                <Label htmlFor={`${tid}filing-notes`}>Filing Notes</Label>
                <DebouncedInput
                  id={`${tid}filing-notes`}
                  value={insuranceFormData.timely_filing_notes || ''}
                  onChange={(e) => setInsuranceFormData({
                    ...insuranceFormData,
                    timely_filing_notes: e.target.value
                  })}
                  placeholder="Special requirements or exceptions..."
                />
              </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        );
        };
        return (
          <>
      <Dialog open={showAddInsurance} onOpenChange={setShowAddInsurance}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Insurance Company</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateInsurance} className="space-y-4">
            {renderInsuranceFormFields('create')}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowAddInsurance(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                Create Insurance
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Insurance Dialog — same shared field renderer as Add. */}
      <Dialog open={showEditInsurance} onOpenChange={setShowEditInsurance}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Insurance Company</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateInsurance} className="space-y-4">
            {renderInsuranceFormFields('edit')}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowEditInsurance(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                Update Insurance
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
          </>
        );
      })()}
    </>
  );
};
